/**
 * Instancing helpers for the dense Community World (Graphics G2).
 *
 * Two strategies, each collapsing N placements of ONE asset into ONE draw call:
 *   • StaticInstancer — repeated STATIC props/foliage, via the engine's
 *     InstancedMeshPool (batches identical geometry+material into one InstancedMesh).
 *   • CrowdInstancer — the DYNAMIC Versona crowd: its own InstancedMesh + a per-frame
 *     count-packing frustum cull (off-screen instances fall outside [0,count) and are
 *     never submitted), reusing the engine's extractFrustumPlanes.
 *
 * Server-authority note: these instance purely DECORATIVE/derived geometry (a crowd of
 * NPCs whose authoritative positions come from `versona` rows; foliage deterministically
 * scattered from a server-declared `instance_group`). No authoritative count or world
 * object is invented client-side — structures/world objects still render per the model.
 */
import { makeAnchorGroup, onRenderAnchor, toAnchorLocal } from './local_origin.js';
import * as THREE from 'three/webgpu';
import { InstancedMeshPool } from 'enginex/render/batching/InstancedMeshPool.js';
import { attribute, float, select, fract, screenCoordinate, vec3, vec4, materialColor, materialRoughness } from 'three/tsl';
import { activePreset } from './quality_tiers.js';
import { FADE_MS } from './lod_fade.js';
import { BatchedMeshPool } from 'enginex/render/batching/BatchedMeshPool.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { RAIN_SHELTER_LAYER } from './rain_shelter.js';
import { seasonTint, seasonTintNode, setSeasonTint } from './season_tint.js';
import { attachCrowdMotion } from './crowd_instancer.js'; // R3 2026-09-25 (materials agent): real crowd motion vectors

/** A sphere (point + `radius`) is inside the 6 normalized inward-facing planes when n·p+d ≥ −radius. */
export function inFrustum(planes: Float32Array, x: number, y: number, z: number, radius: number): boolean {
  for (let i = 0; i < 6; i++) {
    const o = i * 4;
    if (planes[o] * x + planes[o + 1] * y + planes[o + 2] * z + planes[o + 3] < -radius) return false;
  }
  return true;
}

/** The first renderable (geometry, material) under an object — what we instance. */
export function firstMesh(obj: THREE.Object3D): { geometry: THREE.BufferGeometry; material: THREE.Material; mesh: THREE.Mesh } | null {
  let found: { geometry: THREE.BufferGeometry; material: THREE.Material; mesh: THREE.Mesh } | null = null;
  obj.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!found && m.isMesh && m.geometry) found = { geometry: m.geometry, material: Array.isArray(m.material) ? m.material[0] : m.material, mesh: m };
  });
  return found;
}

/**
 * EVERY renderable (geometry, material, local-to-root matrix) under an object — for
 * instancing multi-part GLBs where `firstMesh` would silently drop every part but the
 * first. `root` must be identity-rooted (freshly loaded, never re-parented) so that
 * `child.matrixWorld` IS the child's local-to-root transform; the caller composes that
 * onto a per-placement world matrix to get each part's final instance matrix.
 */
export function collectMeshParts(root: THREE.Object3D): { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4 }[] {
  root.updateMatrixWorld(true);
  const out: { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4 }[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh && m.geometry) {
      out.push({ geometry: m.geometry, material: Array.isArray(m.material) ? m.material[0] : m.material, local: m.matrixWorld.clone() });
    }
  });
  return out;
}

/** Repeated static props/foliage → one InstancedMesh per (geometry, material) = one draw call. */
/**
 * Global "a new instanced batch was born" hook — wired once in main.ts to
 * schedulePipelineWarm. A NEW (geometry, material) batch is a NEW render
 * pipeline, and three r182 compiles pipelines SYNCHRONOUSLY at first view
 * (profiled: multi-second createRenderPipeline bursts while streaming an
 * area). Hooking the single chokepoint every spawner shares (decorations,
 * structures, graves, wilds, admin placements, settlements, foliage) covers
 * them all — including future spawners — with one wiring line.
 */
let newBatchHook: ((mesh?: THREE.Object3D) => void) | null = null;
export function setInstancerWarmHook(fn: ((mesh?: THREE.Object3D) => void) | null): void { newBatchHook = fn; }
export { FADE_MS };

// ── Crossfade support (LOD-switch research S5) ──
// Every batch renders a WRAPPER geometry (the source's attribute objects by
// reference — no data copy — plus one per-instance `aFade` float) with a
// FADE material (the cooked material cloned once, with a screen-door dither:
// interleaved gradient noise n per fragment; incoming aFade ∈ [0,1] keeps
// n < aFade, outgoing aFade ∈ [−2,−1] keeps n ≥ aFade + 2 — lib/lod_fade.ts
// is the JS mirror). At the steady state aFade = 1 keeps everything, so the
// dither costs a compare.
const fadeMats = new WeakMap<THREE.Material, THREE.Material>();
const fadeMatsJ = new WeakMap<THREE.Material, THREE.Material>();

// ── Per-instance JITTER + the SEASON tint (visual-realism R9, 2026-09-25) ────────────────────────────
// A field of one species read as clones: one hue, one value, one roughness. An instancer that opts in
// (foliage, geology — see JITTER_LABELS) carries a second per-instance float `aJit` beside `aFade`, written
// at add() from a hash of the instance's WORLD position (the same plant keeps its look whenever it
// streams back), and its fade material multiplies the colour by a value (±12 %) and warm/cool hue (±5 %)
// swing and the roughness by ±8 %. Opt-in, not global: a settlement building is several instanced PARTS
// at different offsets, and a per-part hash would patch one wall three colours. The extra buffer keeps
// foliage and rocks within WebGPU's 8 (position, normal, uv, aFade, aJit, the instance matrix, the
// previous matrix under the velocity MRT). The switch (`foliageJitter`) applies to materials built after it.
export const JITTER_LABELS: ReadonlySet<string> = new Set(['chunk-foliage', 'world-foliage', 'geology']);
/** The instance's jitter seed in [0, 1) from its world position (0.5 m grid). */
export function jitterSeed(x: number, z: number): number {
  let h = 0x811c9dc5; h ^= Math.round(x * 2); h = Math.imul(h, 0x01000193); h ^= Math.round(z * 2); h = Math.imul(h, 0x01000193);
  h ^= h >>> 13; h = Math.imul(h, 0x5bd1e995); h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}
/** The per-instance tint (value ±12 %, warm/cool hue ±5 %) for a world position — instanceColor users (understory, impostors). */
export function jitterRgb(x: number, z: number): [number, number, number] {
  const j = jitterSeed(x, z), h = (j * 7.31) % 1;
  const v = 0.88 + 0.24 * j, hue = (h - 0.5) * 0.1;
  return [v * (1 + hue), v, v * (1 - hue)];
}
/** The SEASON / dryness tint every foliage material multiplies by (linear RGB; 1,1,1 = the asset's own colour) —
 *  lib/season_tint.ts holds it so the ground's turf layer shares the same uniform. */
export { seasonTint, setSeasonTint };
/** Multiply a foliage material's colour by the season tint (keeps the map's alpha — cards cut out by it). */
export function applySeasonTint(material: THREE.Material): void {
  const m = material as unknown as { colorNode?: unknown; userData: Record<string, unknown> };
  if (m.userData.seasonTint) return;
  const base = (m.colorNode ?? materialColor) as ReturnType<typeof vec4>;
  m.colorNode = vec4(base).mul(vec4(seasonTintNode, 1));
  m.userData.seasonTint = true;
}
// vegetation (2026-09-26): THE CLASSIC-KEY LAW for foliage. A cooked GLB's material is a CLASSIC MeshStandardMaterial;
// three keys it by `customProgramCacheKey()` (onBeforeCompile's source — the same for every classic material) plus its
// scalar props, where a node slot keys as "{}" (RenderObject.getMaterialCacheKey), and NodeManager reuses the FIRST
// build's graph for every later material with that key. A species graph that bakes per-species constants (the
// understory's wind `maxHeight`, a tree's HierarchicalWind `meshHeight` / `meshBaseY`) then draws with the first
// species' numbers. Key such a graph (the ground's `keyGroundGraph` pattern — a serial, so a rebuild never reuses a
// stale build); `fadeMaterialFor` carries the key to its clone. NodeMaterials key their own graphs — never needed there.
let foliageGraphSerial = 0;
export function keyFoliageGraph(material: THREE.Material, tag: string): string {
  const key = `${tag}#${++foliageGraphSerial}`;
  (material as unknown as { customProgramCacheKey: () => string }).customProgramCacheKey = () => key;
  material.userData.graphKey = key;
  return key;
}
// Wrapper geometries are per OWNER (per StaticInstancer), not global.
//
// WHY (2026-09-14): `aFade` is a per-instance attribute that lives on the
// WRAPPER GEOMETRY, and the cache used to be keyed on the source geometry
// alone. Two instancers built over the same cooked GLB — settlement dressing
// and hinterland dressing share plenty — therefore handed their independent
// InstancedMeshes the SAME wrapper, and so the same `aFade` array. Their slot
// allocators are independent, so owner A fading out its slot 3 turned owner
// B's unrelated slot 3 invisible. The first caller's `capacity` also won, so a
// later owner with a larger maxPerBatch got an undersized fade array.
// (Adding capacity to the key would not have fixed it: two owners with equal
// capacity still collide. Ownership is the key.)
interface FadeOwnerState {
  byGeo: Map<THREE.BufferGeometry, THREE.BufferGeometry>;
  offs: Array<() => void>; // source-geometry dispose listeners, removed on release
}
const fadeOwners = new WeakMap<object, FadeOwnerState>();
export function fadeMaterialFor(material: THREE.Material, jitter = false): THREE.Material {
  const cache = jitter ? fadeMatsJ : fadeMats;
  let m = cache.get(material);
  if (m) return m;
  m = material.clone();
  m.name = `${material.name || material.type}#fade${jitter ? 'J' : ''}`;
  // vegetation (2026-09-26): a keyed foliage graph keeps its own key through the clone (clone() drops the override)
  if (typeof material.userData.graphKey === 'string') keyFoliageGraph(m, `${material.userData.graphKey}|fade${jitter ? 'J' : ''}`);
  // A classic Material.clone() copies only the class's own props: the custom
  // node slots the engine layers set on the source (colorNode from the detail
  // surface / snow accumulation, roughnessNode, normalNode …) would be lost on
  // the way into the instancer — the buildings rendered plain (shader wave
  // stage 2 probe, 2026-09-09). Carry every node slot across; the graphs (and
  // their uniforms, e.g. the snow coverage) are shared by design.
  {
    const src = material as unknown as Record<string, unknown>;
    const dst = m as unknown as Record<string, unknown>;
    for (const k of Object.keys(src)) if (k.endsWith('Node') && src[k] != null && dst[k] == null) dst[k] = src[k];
    // material models R4 (2026-09-30): an OWN lighting model (EON diffuse, the flake lobe — engine EONDiffuse.ts) is a
    // method override, not a node slot: clone() drops it and the batch would light the rock with plain Lambert while
    // its key (the copied `diffuseRoughnessNode`) still said EON. Carry it like the slots.
    if (Object.prototype.hasOwnProperty.call(src, 'setupLightingModel') && typeof src.setupLightingModel === 'function') dst.setupLightingModel = src.setupLightingModel;
  }
  try {
    const f = attribute('aFade', 'float');
    const sc = screenCoordinate;
    const n = fract(float(52.9829189).mul(fract(sc.x.mul(0.06711056).add(sc.y.mul(0.00583715)))));
    // Mirror of lib/lod_fade.ts fadeKeep: incoming f ∈ [0,1] keeps n < f; outgoing f ∈ [−2,−1] keeps n ≥ f + 2.
    const keep = select(f.lessThan(-0.5), n.greaterThanEqual(f.add(2)), n.lessThan(f));
    const nm = m as unknown as { opacityNode?: unknown; alphaTestNode?: unknown };
    nm.opacityNode = select(keep, float(1), float(0));
    nm.alphaTestNode = float(0.5);
    if (jitter) {
      // R9: value ±12 %, a warm/cool swing ±5 %, roughness ±8 % — vec4 so a card's alpha survives the multiply
      const j = attribute('aJit', 'float');
      const v = float(0.88).add(j.mul(0.24));
      const hue = fract(j.mul(7.31)).sub(0.5).mul(0.1);
      const cm = m as unknown as { colorNode?: unknown; roughnessNode?: unknown };
      const base = (cm.colorNode ?? materialColor) as ReturnType<typeof vec4>;
      cm.colorNode = vec4(base).mul(vec4(vec3(v.mul(hue.add(1)), v, v.mul(float(1).sub(hue))), 1));
      const rough = (cm.roughnessNode ?? materialRoughness) as ReturnType<typeof float>;
      cm.roughnessNode = float(rough).mul(float(0.92).add(fract(j.mul(3.7)).mul(0.16)));
    }
  } catch { /* a non-node material: no dither, still shares textures */ }
  cache.set(material, m);
  return m;
}
export function fadeGeometryFor(owner: object, geometry: THREE.BufferGeometry, capacity: number, jitter = false): THREE.BufferGeometry {
  let st = fadeOwners.get(owner);
  if (!st) { st = { byGeo: new Map(), offs: [] }; fadeOwners.set(owner, st); }
  const cached = st.byGeo.get(geometry);
  if (cached) return cached;
  const g = new THREE.BufferGeometry();
  for (const name of Object.keys(geometry.attributes)) g.setAttribute(name, geometry.attributes[name]);
  if (geometry.index) g.setIndex(geometry.index);
  g.boundingSphere = geometry.boundingSphere;
  g.boundingBox = geometry.boundingBox;
  // PERF (shader sweep C, 2026-10-06): STATIC usage, not Dynamic. three r185 re-uploads a DynamicDrawUsage attribute on
  // EVERY scene render that draws it (common/Attributes.update: `version < … || usage === DynamicDrawUsage`), and the
  // update range is spent by the first — so every batch re-sent its whole 4,096/16,384-slot aFade (and aJit) EVERY
  // FRAME (and again in any further scene render, a planar reflection), for values that change only on an add, a
  // remove or a crossfade. Every write goes through the pool's setInstanceAttribute / row swap, which bumps the version
  // with a narrow range: one ranged upload in the frame a value changes, nothing otherwise (upload-census bed: 3 batches
  // 49,152 → 0 B on a steady frame, both backends).
  const fade = new THREE.InstancedBufferAttribute(new Float32Array(capacity).fill(1), 1);
  g.setAttribute('aFade', fade);
  const jit = jitter ? new THREE.InstancedBufferAttribute(new Float32Array(capacity).fill(0.5), 1) : null;
  if (jit) g.setAttribute('aJit', jit);
  g.name = `${geometry.name || 'geo'}#fade`;
  // The wrapper dies with its source (its shared attributes are the source's).
  const state = st;
  const onDispose = (): void => { fade.array = new Float32Array(0); if (jit) jit.array = new Float32Array(0); state.byGeo.delete(geometry); };
  geometry.addEventListener('dispose', onDispose);
  st.offs.push(() => geometry.removeEventListener('dispose', onDispose));
  st.byGeo.set(geometry, g);
  return g;
}

/**
 * Drop an owner's wrapper geometries and the dispose listeners they installed
 * on the SOURCE geometries — those sources outlive the instancer (they are the
 * asset cache's), so without this a create/dispose cycle leaves a listener
 * behind on every geometry it ever touched.
 */
export function releaseFadeGeometries(owner: object): void {
  const st = fadeOwners.get(owner);
  if (!st) return;
  for (const off of st.offs) off();
  st.offs.length = 0;
  st.byGeo.clear();
  fadeOwners.delete(owner);
}

export class StaticInstancer {
  private readonly pool: InstancedMeshPool;
  private readonly maxPerBatch: number;
  private nextId = 1;
  // Matrices are ABSOLUTE at the API and anchor-relative in the GPU attribute
  // (lib/local_origin.ts — float32 instance matrices can't ride the scene
  // rebase; 6 cm vertex snapping at 720 km). The batches live under an anchor
  // group; `abs` keeps each instance's world matrix so a rebase can rewrite.
  private readonly anchor: { group: THREE.Group; release: () => void };
  private readonly abs = new Map<number, THREE.Matrix4>();
  private readonly tmp = new THREE.Matrix4();
  private readonly offAnchor: () => void;
  private readonly scene: THREE.Scene;
  /**
   * Shadow policy applied to every batch mesh as it is created (null = leave
   * three's defaults, i.e. neither cast nor receive).
   *
   * WHY A POLICY rather than per-call flags (2026-09-14): a batch is created
   * lazily, per (geometry, material) pair, deep inside `add()`. Settlement
   * buildings ended up casting and receiving NOTHING because no writer ever
   * reached those meshes — a whole town rendered with no shadows on or from
   * it. Setting the intent once on the instancer means a newly-encountered
   * part cannot be missed.
   */
  private shadowPolicy: { cast: boolean; receive: boolean } | null = null;
  /** Render wave 1 (2026-09-17): whether this instancer's batches are ROOFS for the rain-shelter bake (lib/rain_shelter.ts). */
  private rainLayer: boolean | null = null;
  /** R9: this instancer carries the per-instance jitter attribute (JITTER_LABELS). */
  private readonly jitter: boolean;
  /** Whether a source material got the JITTERED fade material (decided once, at its first add — meshFor must agree). */
  private readonly matJitter = new WeakMap<THREE.Material, boolean>();
  constructor(scene: THREE.Scene, maxPerBatch = 4096, label = '') {
    this.scene = scene;
    this.maxPerBatch = maxPerBatch;
    this.jitter = JITTER_LABELS.has(label);
    this.anchor = makeAnchorGroup(`anchor:${label || 'instancer'}`);
    scene.add(this.anchor.group);
    // The pool only ever add()/remove()s its batch meshes on what it is handed.
    this.pool = new InstancedMeshPool(this.anchor.group as unknown as THREE.Scene, maxPerBatch, label);
    this.offAnchor = onRenderAnchor(() => { for (const [id, m] of this.abs) this.pool.updateMatrix(id, toAnchorLocal(m, this.tmp)); });
  }
  /**
   * Add one static instance of (geometry, material) at world `matrix`; returns
   * its handle (or -1 if the batch is full). `fade` is the crossfade value
   * (1 = solid; see lib/lod_fade.ts) — an instance born at 0 is invisible
   * until `setFade` ramps it.
   */
  add(geometry: THREE.BufferGeometry, material: THREE.Material, matrix: THREE.Matrix4, fade = 1): number {
    const id = this.nextId++;
    const before = this.pool.batchCount;
    let mj = this.matJitter.get(material);
    if (mj === undefined) { mj = this.jitter && activePreset().foliageJitter !== false; this.matJitter.set(material, mj); }
    const g = fadeGeometryFor(this, geometry, this.maxPerBatch, this.jitter), m = fadeMaterialFor(material, mj);
    if (this.pool.add(id, g, m, toAnchorLocal(matrix, this.tmp)) < 0) return -1;
    if (this.jitter) this.pool.setInstanceAttribute(id, 'aJit', [jitterSeed(matrix.elements[12], matrix.elements[14])]);
    this.abs.set(id, matrix.clone());
    // ALWAYS write the fade, even the default 1. WHY (2026-09-14): the array is
    // only filled with 1 at creation, and slots are RECYCLED — a slot whose
    // previous occupant faded out still holds its outgoing value (∈ [−2,−1]),
    // so skipping the write on `fade === 1` made the new occupant inherit an
    // invisible dither. One small buffer write per add is not worth a building
    // that never appears.
    this.pool.setInstanceAttribute(id, 'aFade', [fade]);
    // New pipeline → pre-warm JUST this batch (a whole-scene compile pays an
    // O(scene) walk per call — live-profiled as the dominant warm cost).
    if (this.pool.batchCount > before) {
      const batch = this.pool.meshFor(g, m) ?? undefined;
      if (batch && this.shadowPolicy) {
        (batch as THREE.Mesh).castShadow = this.shadowPolicy.cast;
        (batch as THREE.Mesh).receiveShadow = this.shadowPolicy.receive;
      }
      if (batch && this.rainLayer !== null) { if (this.rainLayer) batch.layers.enable(RAIN_SHELTER_LAYER); else batch.layers.disable(RAIN_SHELTER_LAYER); }
      newBatchHook?.(batch);
    }
    return id;
  }

  /**
   * Cast/receive shadows for every batch this instancer owns, now and for any
   * batch created later. Call it once, after construction.
   */
  setShadows(cast: boolean, receive: boolean): void {
    this.shadowPolicy = { cast, receive };
    for (const mesh of this.pool.meshes()) {
      (mesh as THREE.Mesh).castShadow = cast;
      (mesh as THREE.Mesh).receiveShadow = receive;
    }
  }
  /**
   * Put every batch this instancer owns, now and later, on the rain-shelter
   * layer (a ROOF for the top-down bake) — or take them off. Same policy
   * shape as `setShadows`, for the same reason: batches are created lazily.
   */
  setRainLayer(on: boolean): void {
    this.rainLayer = on;
    for (const mesh of this.pool.meshes()) { if (on) mesh.layers.enable(RAIN_SHELTER_LAYER); else mesh.layers.disable(RAIN_SHELTER_LAYER); }
  }
  /** Set an instance's crossfade value (+t incoming, −t outgoing, 1 solid). */
  setFade(id: number, fade: number): void { this.pool.setInstanceAttribute(id, 'aFade', [fade]); }
  /** The batch mesh an instance lives in (null when unknown). */
  batchOf(id: number): THREE.Object3D | null { return this.pool.slotOf(id)?.mesh ?? null; }
  /** The batch mesh backing (geometry, material), if built — targeted pre-compiles. */
  meshFor(geometry: THREE.BufferGeometry, material: THREE.Material): THREE.Object3D | null {
    return this.pool.meshFor(fadeGeometryFor(this, geometry, this.maxPerBatch, this.jitter), fadeMaterialFor(material, this.matJitter.get(material) ?? false));
  }
  remove(id: number): void { this.abs.delete(id); this.pool.remove(id); }
  get instanceCount(): number { return this.pool.totalInstances; }
  get batchCount(): number { return this.pool.batchCount; } // == draw calls for the instanced set
  dispose(): void { this.offAnchor(); this.anchor.release(); this.pool.dispose(); this.abs.clear(); releaseFadeGeometries(this); this.scene.remove(this.anchor.group); }
}

/**
 * Varied static geometry that shares ONE material → one THREE.BatchedMesh = one draw call.
 * All geometries must be consistently indexed (a BatchedMesh requirement) — cooked GLBs are.
 */
export class StaticBatcher {
  private readonly pool: BatchedMeshPool;
  private nextId = 1;
  // Same anchor discipline as StaticInstancer (a BatchedMesh's per-instance
  // matrices are a float32 texture).
  private readonly anchor: { group: THREE.Group; release: () => void };
  private readonly abs = new Map<number, THREE.Matrix4>();
  private readonly tmp = new THREE.Matrix4();
  private readonly offAnchor: () => void;
  private readonly scene: THREE.Scene;
  constructor(scene: THREE.Scene, material?: THREE.Material, maxInstances = 2048, maxVerts = 1 << 19, maxIndices = 1 << 20) {
    this.scene = scene;
    this.anchor = makeAnchorGroup('anchor:batcher');
    scene.add(this.anchor.group);
    this.pool = new BatchedMeshPool(this.anchor.group as unknown as THREE.Scene, maxVerts, maxIndices, maxInstances, material);
    this.offAnchor = onRenderAnchor(() => { for (const [id, m] of this.abs) this.pool.updateMatrix(id, toAnchorLocal(m, this.tmp)); });
  }
  /** Add one instance of `geometry` (deduped internally) at world `matrix`; returns its handle. */
  add(geometry: THREE.BufferGeometry, matrix: THREE.Matrix4): number {
    const geoId = this.pool.addGeometry(geometry); // BatchedMeshPool dedups by geometry.uuid
    const id = this.nextId++;
    this.pool.addInstance(id, geoId, toAnchorLocal(matrix, this.tmp));
    this.abs.set(id, matrix.clone());
    return id;
  }
  remove(id: number): void { this.abs.delete(id); this.pool.remove(id); }
  get instanceCount(): number { return this.pool.instanceCount; }
  get geometryCount(): number { return this.pool.geometryCount; }
  dispose(): void { this.offAnchor(); this.anchor.release(); this.pool.dispose(); this.abs.clear(); this.scene.remove(this.anchor.group); }
}

/** The dynamic Versona crowd: one InstancedMesh + per-frame count-packing frustum cull. */
export class CrowdInstancer {
  readonly mesh: THREE.InstancedMesh;
  private readonly mats: (THREE.Matrix4 | null)[] = [];
  private readonly px: number[] = []; private readonly py: number[] = []; private readonly pz: number[] = [];
  private readonly cullRadius: number;
  private readonly tmpLocal = new THREE.Matrix4();
  private readonly bounds = new THREE.Sphere(new THREE.Vector3(), 0);
  private _visible = 0;
  /** Ground-anchor lift (m) added to the staged ground Y at stamp time: centered
   *  geometry (the capsule) needs ~half its height; GLB avatar crowds pass the
   *  measured −bbox.min.y of their BAKED geometry (≈0 for feet-pivot models). */
  readonly yOffset: number;

  /** R3 (2026-09-25, lib/crowd_instancer.ts): per-slot Δ = previous − current position (WebGPU); null = pinned static. */
  private readonly motion: ReturnType<typeof attachCrowdMotion>;
  constructor(geometry: THREE.BufferGeometry, material: THREE.Material, capacity: number, cullRadius = 1.5, yOffset = 0, label = '') {
    // count-packing reorders slots every frame: three's frozen previous matrices would streak every versona —
    // so the crowd either carries its OWN previous positions (a material clone reading a Δ uniform array,
    // WebGPU) or is pinned static (WebGL2 / the lever `__crowdMotionOff`).
    this.motion = attachCrowdMotion(material, capacity);
    if (!this.motion) pinStaticMotionVectors(material);
    this.mesh = new THREE.InstancedMesh(geometry, this.motion?.material ?? material, capacity);
    if (label) this.mesh.name = `inst:${label}`; // census owner attribution
    // We cull per-instance ourselves against the MAIN camera; three's own test then runs against the bounds of
    // the packed survivors (cull() maintains them), which is what keeps a plaza crowd out of the shadow cascades
    // it is nowhere near — un-cullable, every body was drawn into all four maps.
    this.mesh.boundingSphere = this.bounds;
    this.mesh.frustumCulled = true;
    this.mesh.count = 0;
    this.cullRadius = cullRadius;
    this.yOffset = yOffset;
  }

  /** Drop all logical instances (rebuild each refresh). */
  clear(): void { this.mats.length = 0; this.px.length = 0; this.py.length = 0; this.pz.length = 0; this.motion?.track.beginFrame(performance.now()); }
  /** The matrices themselves outlive clear() (bug sweep 2026-09-22): the slot pool used to BE `mats`,
   *  which clear() truncates every frame — so every versona in every crowd allocated a fresh Matrix4
   *  every frame, the GC churn the comment below was written to prevent. */
  private readonly matPool: THREE.Matrix4[] = [];

  /** Set (matrix) or clear (null) logical instance i's world transform. */
  set(i: number, matrix: THREE.Matrix4 | null): void {
    if (matrix) {
      // Copy into a persistent slot — a clone() per versona per frame was
      // steady GC churn across every crowd (sweep-confirmed).
      const slot = this.matPool[i] ?? (this.matPool[i] = new THREE.Matrix4());
      this.mats[i] = slot;
      slot.copy(matrix);
      this.px[i] = matrix.elements[12]; this.py[i] = matrix.elements[13]; this.pz[i] = matrix.elements[14];
      this.motion?.track.note(i, this.px[i], this.py[i], this.pz[i]);
    } else {
      this.mats[i] = null;
    }
  }

  get logicalCount(): number { let n = 0; for (const m of this.mats) if (m) n++; return n; }
  get visibleCount(): number { return this._visible; }

  /** Pack in-frustum instances to the front of the buffer + set .count → one draw call for the visible crowd. */
  cull(planes: Float32Array): number {
    let v = 0;
    let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    for (let i = 0; i < this.mats.length; i++) {
      const m = this.mats[i];
      if (m && inFrustum(planes, this.px[i], this.py[i], this.pz[i], this.cullRadius)) {
        // Anchor-relative in the attribute (the mesh MUST hang under an anchor
        // group — lib/local_origin.ts); px/py/pz stay absolute for the cull.
        const e = toAnchorLocal(m, this.tmpLocal).elements;
        this.mesh.setMatrixAt(v, this.tmpLocal);
        this.motion?.track.pack(v, i);
        if (e[12] < x0) x0 = e[12]; if (e[12] > x1) x1 = e[12];
        if (e[13] < y0) y0 = e[13]; if (e[13] > y1) y1 = e[13];
        if (e[14] < z0) z0 = e[14]; if (e[14] > z1) z1 = e[14];
        v++;
      }
    }
    this.mesh.count = v;
    this._visible = v;
    // The survivors' bounds, in the mesh's own (anchor-local) space: the box of their origins + a body's reach
    // (2× the cull radius: an instance origin is the FEET, and a scaled body stands up to ~2 m above them).
    if (v > 0) {
      this.bounds.center.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
      this.bounds.radius = Math.hypot(x1 - x0, y1 - y0, z1 - z0) / 2 + this.cullRadius * 2;
    }
    // Upload ONLY the packed prefix. Flagging the whole attribute re-sent the
    // FULL capacity buffer (1024 × 64 B) per crowd per frame even with zero
    // visible NPCs — ~78 MB/s of bufferSubData across the avatar crowds
    // (sweep-confirmed; the potato-walk profile's #2 blocker). count = 0 draws
    // nothing, so no upload is needed at all.
    if (v > 0) {
      const attr = this.mesh.instanceMatrix;
      attr.clearUpdateRanges();
      attr.addUpdateRange(0, v * 16);
      attr.needsUpdate = true;
      const dn = this.motion?.node; if (dn) { dn.clearUpdateRanges?.(); dn.addUpdateRange?.(0, v * 4); }
    }
    return v;
  }

  dispose(): void { this.mesh.dispose(); if (this.motion) this.motion.material.dispose(); }
}
